import React, { useEffect, useState } from 'react';
import { Box, Button, Paper } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Logo, LogoDarkMode } from '../../assets/images';
import { SocialProvider } from '../../types/auth';
import Step1Email from './steps/Step1Email';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';
import Step4 from './steps/Step4';
import Step5 from './steps/Step5';
import {
  clearPreSignupFlowState,
  loadPreSignupFlowState,
  savePreSignupCompleted,
  savePreSignupFlowState,
} from './preSignupSession';

export interface PreSignupFlowMeta {
  role: string;
  goals: string[];
  experience: string[];
}

interface PreSignupFlowProps {
  onComplete: (email: string, meta?: PreSignupFlowMeta) => void;
  onSocialSignIn: (provider: SocialProvider) => void;
}

export default function PreSignupFlow({ onComplete, onSocialSignIn }: PreSignupFlowProps) {
  const navigate = useNavigate();
  const [flow, setFlow] = useState(() => loadPreSignupFlowState());

  useEffect(() => {
    savePreSignupFlowState(flow);
  }, [flow]);

  const { step, email, role, goals, experience, ghlContactId } = flow;
  const emailInitial = (email.trim()[0] || '').toUpperCase();

  const handleStep1Complete = (submittedEmail: string, ghlContactId: string) => {
    setFlow((prev) => ({ ...prev, email: submittedEmail, ghlContactId: ghlContactId, step: 2 }));
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <Step1Email
            email={email}
            onEmailChange={(v) => setFlow((p) => ({ ...p, email: v }))}
            onComplete={(submittedEmail, ghlContactId) => handleStep1Complete(submittedEmail, ghlContactId)}
            onSocialSignIn={onSocialSignIn}
          />
        );
      case 2:
        return (
          <Step2
            email={email}
            onComplete={(selectedRole) =>
              setFlow((p) => ({ ...p, role: selectedRole, step: 3, ghlContactId: ghlContactId }))
            }
            onBack={() => setFlow((p) => ({ ...p, step: 1 }))}
            ghlContactId={ghlContactId}
          />
        );
      case 3:
        return (
          <Step3
            email={email}
            onComplete={(selectedGoals) =>
              setFlow((p) => ({ ...p, goals: selectedGoals, step: 4, ghlContactId: ghlContactId }))
            }
            onBack={() => setFlow((p) => ({ ...p, step: 2 }))}
            ghlContactId={ghlContactId}
          />
        );
      case 4:
        return (
          <Step4
            email={email}
            onComplete={(exp) => setFlow((p) => ({ ...p, experience: exp, step: 5, ghlContactId: ghlContactId }))}
            onBack={() => setFlow((p) => ({ ...p, step: 3, ghlContactId: ghlContactId }))}
            ghlContactId={ghlContactId}
          />
        );
      case 5:
        return (
          <Step5
            onComplete={() => {
              savePreSignupCompleted({ email, role, goals, experience });
              clearPreSignupFlowState();
              onComplete(email, { role, goals, experience });
            }}
            onBack={() => setFlow((p) => ({ ...p, step: 4, ghlContactId: ghlContactId }))}
            ghlContactId={ghlContactId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: { xs: '16px 24px', sm: '24px 32px' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <img
            src={Logo}
            alt="logo"
            style={{ height: 24, cursor: 'pointer', maxWidth: '100%' }}
            onClick={() => window.open('https://easywebinar.com/', '_blank')}
          />
        </Box>
        {step === 1 ? (
          <Button
            variant="contained"
            endIcon={<ArrowForward />}
            size="small"
            onClick={() => navigate('/login')}
            sx={{
              bgcolor: '#6066E9',
              color: '#fff',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#4B4EB5' },
            }}
          >
            Login
          </Button>
        ) : (
          <Box
            aria-label="signup-email-initial"
            sx={{
              height: 36,
              width: 36,
              borderRadius: '999px',
              bgcolor: 'rgba(96, 102, 233, 0.12)',
              color: '#6066E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.95rem',
              userSelect: 'none',
            }}
          >
            {emailInitial || 'U'}
          </Box>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          // alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: step === 5 ? { xs: '100%', sm: 780 } : 480,
            borderRadius: '20px',
            // border: '1px solid rgba(16, 24, 40, 0.08)',
            // boxShadow: '0 24px 64px rgba(16, 24, 40, 0.16)',
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4.5 },
            bgcolor: '#FFFFFF',
          }}
        >
          {renderStep()}
        </Paper>
      </Box>
    </Box>
  );
}
